import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, Archive, CheckCircle2, Clock, Loader2, Shield } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useProvider } from "@/contexts/ProviderContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useJotformPlanMatch } from "@/hooks/useJotformPlanMatch";
import { getBillingStatusLabel } from "@/lib/billingAccess";
import { formatGraceDeadline, getGraceDaysRemaining } from "@/lib/jotformPlanGrace";
import {
  determinePlanFlow,
  getTierIdFromSubscription,
  type PlanFlowMode,
  type PriceIdKey,
  type TierId,
} from "@/lib/planFlow";
import { logger } from "@/lib/logger";
import { FUNNEL_EVENTS, trackEvent } from "@/lib/analytics";

type SelectTierAction = PlanFlowMode | null;

const TIER_TO_PRICE_KEY: Record<"tier_1" | "tier_2" | "tier_3" | "tier_4", PriceIdKey> = {
  tier_1: "starter",
  tier_2: "bronze_monthly",
  tier_3: "silver_monthly",
  tier_4: "gold_monthly",
};

const PRICE_COPY: Record<PriceIdKey, { price: string; frequency: string }> = {
  starter: { price: "$20", frequency: "one-time after trial" },
  bronze_monthly: { price: "$8", frequency: "per month" },
  bronze_annual: { price: "$80", frequency: "per year" },
  silver_monthly: { price: "$12", frequency: "per month" },
  silver_annual: { price: "$120", frequency: "per year" },
  gold_monthly: { price: "$25", frequency: "per month" },
  gold_annual: { price: "$250", frequency: "per year" },
};

function getRequestedAction(rawValue: string | null): SelectTierAction {
  switch (rawValue) {
    case "activate":
    case "reinstate":
    case "upgrade":
    case "current":
    case "contact":
      return rawValue;
    default:
      return null;
  }
}

function formatCurrencyAmount(amountInCents: number, currency: string | null | undefined): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
  }).format(amountInCents / 100);
}

const SelectTierPage = () => {
  const { user, subscription, isSubscriptionLoading, fetchSubscriptionStatus } = useAuth();
  const { connectionStatus, isCheckingConnections } = useProvider();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const jotformPlanMatch = useJotformPlanMatch();
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);
  const [showProrationDialog, setShowProrationDialog] = useState(false);
  const [prorationDetails, setProrationDetails] = useState<{
    proration_amount_due: number;
    proration_currency: string;
    lines?: Array<{
      description: string;
      amount: number;
      period: { start: number; end: number };
    }>;
  } | null>(null);
  const [targetPriceIdKeyForUpdate, setTargetPriceIdKeyForUpdate] = useState<PriceIdKey | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);

  const requestedAction = getRequestedAction(searchParams.get("action"));
  const matchedTierId = (jotformPlanMatch.data?.matchedTier.id as TierId | undefined) || null;
  const planDecision = determinePlanFlow({
    currentTierId: getTierIdFromSubscription(subscription),
    matchedTierId,
    currentPriceIdKey: subscription?.active_price_id_key || null,
    subscribed: subscription?.subscribed,
    billingHasHistory: subscription?.billing_has_history,
    billingAccessActive: subscription?.billing_access_active,
  });
  const targetPriceIdKey = planDecision.targetPriceIdKey
    || (matchedTierId && matchedTierId !== "tier_5" ? TIER_TO_PRICE_KEY[matchedTierId] : null);
  const priceCopy = targetPriceIdKey ? PRICE_COPY[targetPriceIdKey] : null;
  const currentCycle = subscription?.active_price_id_key?.includes("annual") ? "Annual" : "Monthly";
  const actionWasRerouted = requestedAction && requestedAction !== planDecision.mode;
  const isConnected = connectionStatus.jotform;
  const isLoading = isSubscriptionLoading
    || isCheckingConnections
    || isConnected === null
    || (isConnected === true && jotformPlanMatch.isLoading);
  const accountStatus = jotformPlanMatch.data?.status || null;
  const billingStatusLabel = getBillingStatusLabel(subscription?.billing_status);
  const graceEndsLabel = formatGraceDeadline(jotformPlanMatch.data?.graceEndsAt);
  const graceDaysRemaining = getGraceDaysRemaining(jotformPlanMatch.data?.graceEndsAt);
  const upgradeRequired = Boolean(jotformPlanMatch.data?.upgradeRequired);
  const mismatchEnforced = upgradeRequired && Boolean(jotformPlanMatch.data?.enforcementActive);

  useEffect(() => {
    if (searchParams.get("checkout_canceled") !== "true") return;

    toast({
      title: "Checkout Canceled",
      description: "No billing change was made.",
    });

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("checkout_canceled");
    const nextQuery = nextParams.toString();
    navigate({ pathname: "/select-tier", search: nextQuery ? `?${nextQuery}` : "" }, { replace: true });
  }, [navigate, searchParams]);

  const handleOpenBillingPortal = async () => {
    setPageError(null);
    setIsPortalLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw new Error(data?.error || error.message || "Failed to open billing portal");
      if (!data?.url) throw new Error("Could not retrieve customer portal URL.");
      window.open(data.url, "_blank");
    } catch (error: unknown) {
      logger.error("SelectTierPage: customer portal error", error);
      setPageError(error instanceof Error ? error.message : "Failed to open billing portal.");
    } finally {
      setIsPortalLoading(false);
    }
  };

  const handleCheckout = async (replaceExistingSubscription = false) => {
    if (!user) {
      navigate("/auth");
      return;
    }

    if (!matchedTierId || !targetPriceIdKey) {
      setPageError("No self-serve plan is available for this account.");
      return;
    }

    setPageError(null);
    setIsCheckoutLoading(true);

    try {
      trackEvent(FUNNEL_EVENTS.CHECKOUT_START, {
        plan: targetPriceIdKey,
        tier: matchedTierId,
        mode: planDecision.mode,
      });

      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: {
          priceIdKey: targetPriceIdKey,
          tierId: matchedTierId,
          replaceExistingSubscription,
        },
      });

      if (error) throw new Error(data?.error || error.message);
      if (!data?.url) throw new Error("No checkout URL returned.");
      window.location.href = data.url;
    } catch (error: unknown) {
      logger.error("SelectTierPage: checkout error", error);
      setPageError(error instanceof Error ? error.message : "Could not start checkout.");
      setIsCheckoutLoading(false);
    }
  };

  const handlePreviewUpgrade = async () => {
    if (!targetPriceIdKey) {
      setPageError("No target plan is available for this upgrade.");
      return;
    }

    setPageError(null);
    setIsUpdateLoading(true);
    setTargetPriceIdKeyForUpdate(targetPriceIdKey);

    try {
      const { data, error } = await supabase.functions.invoke("update-subscription-plan", {
        body: { newPriceIdKey: targetPriceIdKey, confirmUpdate: false },
      });

      if (error) throw new Error(data?.error || error.message);
      setProrationDetails(data || null);
      setShowProrationDialog(true);
    } catch (error: unknown) {
      logger.error("SelectTierPage: upgrade preview error", error);
      setPageError(error instanceof Error ? error.message : "Could not prepare the upgrade.");
      setTargetPriceIdKeyForUpdate(null);
    } finally {
      setIsUpdateLoading(false);
    }
  };

  const confirmUpgrade = async () => {
    if (!targetPriceIdKeyForUpdate) return;

    setPageError(null);
    setShowProrationDialog(false);
    setIsUpdateLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("update-subscription-plan", {
        body: { newPriceIdKey: targetPriceIdKeyForUpdate, confirmUpdate: true },
      });

      if (error) throw new Error(data?.error || error.message);

      toast({
        title: "Plan updated",
        description: "Your FAB subscription is now aligned with Jotform.",
      });
      await fetchSubscriptionStatus();
      await jotformPlanMatch.refetch();
    } catch (error: unknown) {
      logger.error("SelectTierPage: upgrade confirm error", error);
      setPageError(error instanceof Error ? error.message : "Could not update your plan.");
    } finally {
      setTargetPriceIdKeyForUpdate(null);
      setIsUpdateLoading(false);
    }
  };

  const renderPrimaryAction = () => {
    switch (planDecision.mode) {
      case "activate":
        return (
          <Button className="w-full" size="lg" onClick={() => handleCheckout(false)} disabled={isCheckoutLoading}>
            {isCheckoutLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Redirecting to checkout...
              </>
            ) : (
              "Start 14-Day Free Trial"
            )}
          </Button>
        );

      case "reinstate":
        return (
          <Button className="w-full" size="lg" onClick={() => handleCheckout(true)} disabled={isCheckoutLoading}>
            {isCheckoutLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Preparing reinstatement...
              </>
            ) : (
              "Reinstate Plan"
            )}
          </Button>
        );

      case "upgrade":
        return (
          <Button className="w-full" size="lg" onClick={handlePreviewUpgrade} disabled={isUpdateLoading}>
            {isUpdateLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Reviewing upgrade...
              </>
            ) : (
              "Review Upgrade"
            )}
          </Button>
        );

      case "contact":
        return (
          <Button asChild className="w-full" size="lg">
            <a href="mailto:support@fab.day?subject=Enterprise%20Plan%20Inquiry">Contact Us</a>
          </Button>
        );

      case "current":
      default:
        return (
          <Button className="w-full" size="lg" onClick={() => navigate("/dashboard")}>
            Return to Dashboard
          </Button>
        );
    }
  };

  const getHeadline = (): string => {
    switch (planDecision.mode) {
      case "activate":
        return "Activate your matched FAB plan";
      case "reinstate":
        return "Reinstate your matched FAB plan";
      case "upgrade":
        return "Upgrade FAB to match Jotform";
      case "contact":
        return "Enterprise activation";
      case "current":
      default:
        return "Your matched FAB plan";
    }
  };

  const getDescription = (): string => {
    if (!jotformPlanMatch.data) {
      return "We use your Jotform subscription to determine the right FAB plan.";
    }

    switch (planDecision.mode) {
      case "activate":
        return `Your Jotform ${jotformPlanMatch.data.planSlug} account maps to ${jotformPlanMatch.data.matchedTier.displayName}. Start billing on that tier with a full 14-day trial.`;
      case "reinstate":
        return `Your FAB billing is currently ${billingStatusLabel.toLowerCase()}. Reinstating will put you back on the ${jotformPlanMatch.data.matchedTier.displayName} plan that matches your Jotform account today.`;
      case "upgrade":
        return `Your Jotform account now maps to ${jotformPlanMatch.data.matchedTier.displayName}. Upgrade FAB so the account stays aligned.`;
      case "contact":
        return "Your Jotform account maps to Enterprise. We handle those activations directly.";
      case "current":
      default:
        return `FAB is already aligned to your Jotform ${jotformPlanMatch.data.planSlug} account.`;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <div className="flex-grow container mx-auto px-4 py-12">
        <header className="mx-auto mb-10 max-w-3xl text-center">
          <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
            {getHeadline()}
          </h1>
          <p className="mt-3 text-xl text-muted-foreground">
            {getDescription()}
          </p>
        </header>

        {isLoading && (
          <div className="flex flex-col items-center gap-3 py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Loading your matched plan...</p>
          </div>
        )}

        {!isLoading && !user && (
          <div className="mx-auto max-w-lg">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Sign in required</AlertTitle>
              <AlertDescription>
                Sign in first so FAB can look up your Jotform connection and billing state.
              </AlertDescription>
            </Alert>
          </div>
        )}

        {!isLoading && user && isConnected === false && (
          <div className="mx-auto max-w-lg">
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Jotform not connected</AlertTitle>
              <AlertDescription>
                Reconnect Jotform first so we can match the correct FAB plan.
              </AlertDescription>
            </Alert>
            <Button className="w-full" onClick={() => navigate("/connect-jotform")}>
              Reconnect Jotform
            </Button>
          </div>
        )}

        {!isLoading && user && isConnected === true && jotformPlanMatch.error && (
          <div className="mx-auto max-w-lg">
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Could not read your Jotform plan</AlertTitle>
              <AlertDescription>
                {jotformPlanMatch.error instanceof Error
                  ? jotformPlanMatch.error.message
                  : "Please reconnect Jotform and try again."}
              </AlertDescription>
            </Alert>
            <Button className="w-full" onClick={() => navigate("/connect-jotform")}>
              Reconnect Jotform
            </Button>
          </div>
        )}

        {!isLoading && user && isConnected === true && jotformPlanMatch.data && accountStatus !== "ACTIVE" && (
          <div className="mx-auto max-w-lg">
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Jotform account inactive</AlertTitle>
              <AlertDescription>
                Your Jotform account is currently marked {accountStatus}. FAB requires an active Jotform account.
              </AlertDescription>
            </Alert>
            <Button className="w-full" onClick={() => navigate("/dashboard")}>
              Return to Dashboard
            </Button>
          </div>
        )}

        {!isLoading && user && isConnected === true && jotformPlanMatch.data && accountStatus === "ACTIVE" && (
          <div className="mx-auto max-w-3xl space-y-6">
            {actionWasRerouted && (
              <Alert className="border-blue-200 bg-blue-50 text-blue-950 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-100">
                <CheckCircle2 className="h-4 w-4" />
                <AlertTitle>Account state updated</AlertTitle>
                <AlertDescription>
                  FAB recalculated this page from your current Stripe and Jotform state, so the requested action was adjusted to the correct next step.
                </AlertDescription>
              </Alert>
            )}

            {pageError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{pageError}</AlertDescription>
              </Alert>
            )}

            {upgradeRequired && !mismatchEnforced && (
              <Alert className="border-blue-200 bg-blue-50 text-blue-950 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-100">
                <Clock className="h-4 w-4" />
                <AlertTitle>Upgrade grace period active</AlertTitle>
                <AlertDescription>
                  {graceEndsLabel
                    ? `Your Jotform account is already on a higher tier. Upgrade FAB by ${graceEndsLabel}${graceDaysRemaining !== null ? ` (${graceDaysRemaining} day${graceDaysRemaining === 1 ? "" : "s"} remaining)` : ""} to keep every form link and backup-history view available.`
                    : "Your Jotform account is already on a higher tier. Upgrade FAB within the 14-day grace period to keep every form link and backup-history view available."}
                </AlertDescription>
              </Alert>
            )}

            {upgradeRequired && mismatchEnforced && (
              <Alert className="border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Upgrade required</AlertTitle>
                <AlertDescription>
                  The Jotform grace period
                  {graceEndsLabel ? ` ended on ${graceEndsLabel}` : " has ended"}.
                  Forms beyond your current FAB visibility limit are now greyed out and backup history stays hidden until FAB is upgraded, but automated backups still continue for every form.
                </AlertDescription>
              </Alert>
            )}

            <Card className="border-primary/40 shadow-lg">
              <CardHeader className="space-y-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <CardTitle className="text-3xl font-bold">
                      {jotformPlanMatch.data.matchedTier.displayName}
                    </CardTitle>
                    <CardDescription className="text-base">
                      Matched from your Jotform {jotformPlanMatch.data.planSlug.toLowerCase()} plan
                    </CardDescription>
                  </div>
                  {priceCopy && (
                    <div className="rounded-2xl border border-border bg-muted/50 px-4 py-3 text-left sm:text-right">
                      <p className="text-sm text-muted-foreground">Billing</p>
                      <p className="text-2xl font-semibold text-foreground">{priceCopy.price}</p>
                      <p className="text-sm text-muted-foreground">{priceCopy.frequency}</p>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="rounded-xl border border-border bg-card p-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Archive className="h-4 w-4 text-primary" />
                      Forms
                    </div>
                    <p className="mt-2 text-2xl font-semibold text-foreground">
                      {jotformPlanMatch.data.matchedTier.formLimit}
                    </p>
                    <p className="text-sm text-muted-foreground">covered in the FAB UI</p>
                  </div>
                  <div className="rounded-xl border border-border bg-card p-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="h-4 w-4 text-primary" />
                      Visible history
                    </div>
                    <p className="mt-2 text-2xl font-semibold text-foreground">
                      {jotformPlanMatch.data.matchedTier.retentionDays} days
                    </p>
                    <p className="text-sm text-muted-foreground">shown in the app</p>
                  </div>
                  <div className="rounded-xl border border-border bg-card p-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Shield className="h-4 w-4 text-primary" />
                      Current FAB billing
                    </div>
                    <p className="mt-2 text-2xl font-semibold text-foreground">
                      {subscription?.subscription_tier || "Starter"}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {subscription?.subscribed
                        ? `${currentCycle} billing`
                        : billingStatusLabel}
                    </p>
                  </div>
                </div>

                <div className="space-y-3 rounded-xl border border-border bg-muted/30 p-4">
                  <p className="font-medium text-foreground">What happens next</p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      All tiers include a 14-day trial the first time the customer activates billing.
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      FAB billing will match your current Jotform tier, not a generic pricing page choice.
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      If your plan is inactive, reinstating here replaces the old inactive Stripe subscription before starting the matched one.
                    </li>
                  </ul>
                </div>

                <div className="space-y-3">
                  {renderPrimaryAction()}
                  {(subscription?.billing_has_history || subscription?.stripe_customer_id) && (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={handleOpenBillingPortal}
                      disabled={isPortalLoading}
                    >
                      {isPortalLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Opening billing portal...
                        </>
                      ) : (
                        "Manage Billing"
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {prorationDetails && (
        <AlertDialog open={showProrationDialog} onOpenChange={setShowProrationDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm plan change</AlertDialogTitle>
              <AlertDialogDescription>
                {prorationDetails.proration_amount_due > 0
                  ? `You will be charged approximately ${formatCurrencyAmount(prorationDetails.proration_amount_due, prorationDetails.proration_currency)} immediately.`
                  : `A credit of approximately ${formatCurrencyAmount(Math.abs(prorationDetails.proration_amount_due), prorationDetails.proration_currency)} will be applied to your account.`}
                {prorationDetails.lines?.map((line, index) => (
                  <div key={`${line.description}-${index}`} className="mt-2 text-xs text-muted-foreground">
                    {line.description}: {formatCurrencyAmount(line.amount, prorationDetails.proration_currency)}
                  </div>
                ))}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setTargetPriceIdKeyForUpdate(null)}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction onClick={confirmUpgrade} disabled={isUpdateLoading}>
                {isUpdateLoading ? "Processing..." : "Confirm & Pay"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      <Footer />
    </div>
  );
};

export default SelectTierPage;
